import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL } from "@/data/services";

const PHONE_DISPLAY = "0712 196 560";
const PHONE_TEL = "+255712196560";

const NAV = [
  { label: "Home", href: "/#home" },
  { label: "Services", href: "/#services" },
  { label: "About", href: "/about", page: true },
  { label: "Team", href: "/team", page: true },
  { label: "Gallery", href: "/gallery", page: true },
  // { label: "Blog", href: "/blog", page: true },
  { label: "Certs", href: "/certs", page: true },
  // { label: "Pricing", href: "/#pricing" },
  { label: "Contact", href: "/#contact" },
];

export const Navbar = ({ onOrder }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState("home");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy so section links (Home/Services/Pricing/Contact) get the same active-underline as page links.
  useEffect(() => {
    if (location.pathname !== "/") return;
    const ids = ["home", "services", "pricing", "contact"];
    const onScroll = () => {
      let current = "home";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) current = id;
      }
      setActiveHash(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const isActive = (n) =>
    n.page ? location.pathname === n.href : location.pathname === "/" && n.href === `/#${activeHash}`;

  const go = (href) => {
    setOpen(false);
    if (!href.includes("#")) {
      navigate(href);
      window.scrollTo(0, 0);
      return;
    }
    const [, hash] = href.split("#");
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-brand-dark border-b border-white/10 transition-shadow duration-300 ${
        scrolled ? "shadow-lg shadow-brand-dark/30" : ""
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          onClick={() => { setOpen(false); window.scrollTo(0, 0); }}
          className="flex items-center gap-3 shrink-0"
        >
          <img src={LOGO_URL} alt="Tanz-Bridge — Commitment for Excellence" className="h-16 w-auto object-contain rounded-lg bg-white px-3 py-2" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => {
            const active = isActive(n);
            return (
              <button
                key={n.label}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                onClick={() => go(n.href)}
                className={`font-body text-[15.2px] font-medium tracking-normal underline-offset-8 decoration-2 transition-colors duration-200 ${
                  active
                    ? "text-brand-yellow underline"
                    : "text-gray-200 hover:text-brand-yellow hover:underline"
                }`}
              >
                {n.label}
              </button>
            );
          })}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="nav-phone-btn"
            className="ml-12 inline-flex items-center gap-2 bg-brand-yellow text-brand-dark font-body font-semibold text-[14.4px] px-5 py-2.5 rounded-lg hover:brightness-95 transition-all duration-200 shadow-md"
          >
            <Phone size={18} strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-brand-dark border-b border-white/10 px-5 py-6 flex flex-col gap-4">
          {NAV.map((n) => {
            const active = isActive(n);
            return (
              <button
                key={n.label}
                data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                onClick={() => go(n.href)}
                className={`text-left text-lg font-semibold py-1 underline-offset-8 decoration-2 ${
                  active
                    ? "text-brand-yellow underline"
                    : "text-gray-200 hover:text-brand-yellow hover:underline"
                }`}
              >
                {n.label}
              </button>
            );
          })}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="nav-mobile-phone-btn"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-dark font-heading font-bold text-lg px-5 py-3 rounded-lg hover:brightness-95 transition-all duration-200 shadow-md"
          >
            <Phone size={20} strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </a>
        </div>
      )}
    </header>
  );
};
